import React from 'react';
import type { ComponentType } from 'react';
import type {
  AssistantMessage as AgUiAssistantMessage,
  Message,
  UserMessage as AgUiUserMessage,
} from '@ag-ui/client';
import { AG_UI_ACTIVITY_TYPE, AG_UI_ROLE } from '../agent/ag-ui.constants';

export interface AssistantMessageProps {
  message: AgUiAssistantMessage;
  isLast: boolean;
  /** True when the next message carries an ASA offer — the local run's refusal is part of that offer. */
  precedesOffer?: boolean;
}

interface ChatMessageComponents {
  UserMessage: ComponentType<{ message: AgUiUserMessage }>;
  AssistantMessage: ComponentType<AssistantMessageProps>;
  ProgressLoader: ComponentType<{ status: string }>;
}

interface ChatMessageProps {
  message: Message;
  isLast: boolean;
  precedesOffer?: boolean;
}

/**
 * Builds a platform's ChatMessage from its rendered components: one shared
 * routing switch over `agent.messages[i]` (user → question bubble, assistant →
 * answer, activity status → progress line), per-platform rendering.
 */
export function createChatMessage({
  UserMessage,
  AssistantMessage,
  ProgressLoader,
}: ChatMessageComponents) {
  return function ChatMessage({ message, isLast, precedesOffer }: ChatMessageProps) {
    switch (message.role) {
      case AG_UI_ROLE.USER:
        return <UserMessage message={message} />;

      case AG_UI_ROLE.ASSISTANT:
        return <AssistantMessage message={message} isLast={isLast} precedesOffer={precedesOffer} />;

      case AG_UI_ROLE.ACTIVITY: {
        if (message.activityType !== AG_UI_ACTIVITY_TYPE.STATUS) return null;
        const text = typeof message.content?.text === 'string' ? message.content.text : '';
        return text ? <ProgressLoader status={text} /> : null;
      }

      default:
        return null;
    }
  };
}
